<template>
  <BaseTemplate app-name="Eventos" page-title="">
    <template #content>
      <div class="min-h-screen bg-slate-100 px-3 py-4 transition-colors duration-300 dark:bg-gray-900 sm:px-4 lg:px-6">
        <div class="mx-auto max-w-[1600px] space-y-4">

          <!-- ====== CABEÇALHO ====== -->
          <header class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div class="min-w-0">
              <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Eventos</h1>
              <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Manutenções e ocorrências dos tomógrafos ·
                <span class="font-medium text-gray-700 dark:text-gray-200">{{ abertos.length }}</span> em aberto de {{ eventos.length }}
                <span v-if="ultimaAtualizacao"> · atualizado às {{ horaUltimaAtualizacao }}</span>
                <span v-if="erroCarregamento" class="ml-1 font-medium text-red-600 dark:text-red-400">· falha na última atualização</span>
              </p>
            </div>

            <div class="flex flex-wrap items-center gap-2">
              <label class="relative min-w-0 flex-1 lg:w-72">
                <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input v-model.trim="busca" type="search" class="input pl-9" placeholder="Buscar por unidade, equipamento, motivo ou descrição" aria-label="Buscar eventos" />
              </label>
              <button type="button" class="btn-secondary shrink-0" :disabled="isRefreshing" aria-label="Atualizar" @click="atualizar">
                <RefreshCw class="h-4 w-4" :class="{ 'animate-spin': isRefreshing }" />
                <span class="hidden sm:inline">Atualizar</span>
              </button>
              <button v-if="podeGerir" type="button" class="btn-primary shrink-0" @click="abrirCriacao">
                <Plus class="h-4 w-4" />
                Novo evento
              </button>
            </div>
          </header>

          <!-- ====== INDICADORES ====== -->
          <section class="grid grid-cols-2 gap-3 xl:grid-cols-4" aria-label="Indicadores">
            <button
              v-for="kpi in kpis"
              :key="kpi.key"
              type="button"
              class="card group flex items-center gap-3 p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md sm:p-4"
              :class="kpiAtivo(kpi) ? 'ring-2 ring-offset-2 ring-offset-slate-100 dark:ring-offset-gray-900 ' + kpi.ring : ''"
              :aria-pressed="kpiAtivo(kpi)"
              @click="alternarKpi(kpi)"
            >
              <span class="rounded-lg p-2" :class="kpi.iconBg">
                <component :is="kpi.icon" class="h-5 w-5" :class="kpi.iconColor" />
              </span>
              <div class="min-w-0">
                <p class="text-2xl font-bold leading-none text-gray-900 dark:text-white">{{ kpi.valor }}</p>
                <p class="mt-1 truncate text-xs font-medium text-gray-500 dark:text-gray-400">{{ kpi.label }}</p>
              </div>
            </button>
          </section>

          <!-- ====== FILTROS ====== -->
          <section class="card p-3 sm:p-4" aria-label="Filtros">
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <label class="block">
                <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Unidade</span>
                <select v-model="filtros.unidade" class="input">
                  <option value="">Todas as unidades</option>
                  <option v-for="u in unidades" :key="u.id" :value="u.id">{{ u.nome }}</option>
                </select>
              </label>
              <label class="block">
                <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Categoria</span>
                <select v-model="filtros.categoria" class="input">
                  <option value="">Todas as categorias</option>
                  <option v-for="(meta, chave) in CATEGORIA_META" :key="chave" :value="chave">{{ meta.longo }}</option>
                </select>
              </label>
              <label class="block">
                <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Status</span>
                <select v-model="filtros.status" class="input">
                  <option value="">Todos os status</option>
                  <option v-for="(meta, chave) in STATUS_META" :key="chave" :value="chave">{{ meta.label }}</option>
                </select>
              </label>
              <label class="block">
                <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Período (abertura)</span>
                <select v-model="filtros.periodo" class="input">
                  <option value="">Todo o período</option>
                  <option value="7">Últimos 7 dias</option>
                  <option value="30">Últimos 30 dias</option>
                  <option value="90">Últimos 90 dias</option>
                  <option value="365">Últimos 12 meses</option>
                </select>
              </label>
            </div>

            <div class="mt-3 flex flex-wrap items-center gap-2">
              <button
                type="button"
                class="badge border px-3 py-1 transition"
                :class="filtros.atrasados ? 'border-red-600 bg-red-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'"
                :aria-pressed="filtros.atrasados"
                @click="filtros.atrasados = !filtros.atrasados"
              >
                <AlertTriangle class="h-3.5 w-3.5" /> Atrasados
                <span class="rounded-full bg-black/10 px-1.5 text-[10px] dark:bg-white/10">{{ totalAtrasados }}</span>
              </button>
              <button
                type="button"
                class="badge border px-3 py-1 transition"
                :class="filtros.paradas ? 'border-red-600 bg-red-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'"
                :aria-pressed="filtros.paradas"
                @click="filtros.paradas = !filtros.paradas"
              >
                <OctagonAlert class="h-3.5 w-3.5" /> Equipamento parado
                <span class="rounded-full bg-black/10 px-1.5 text-[10px] dark:bg-white/10">{{ totalParadas }}</span>
              </button>
              <p class="ml-auto text-xs text-gray-500 dark:text-gray-400">
                {{ eventosFiltrados.length }} de {{ eventos.length }} eventos
                <button v-if="haFiltros" type="button" class="ml-2 font-medium text-blue-600 hover:underline dark:text-blue-400" @click="limparFiltros">Limpar filtros</button>
              </p>
            </div>
          </section>

          <!-- ====== LISTA ====== -->
          <div v-if="isLoading" class="space-y-2" aria-busy="true">
            <div v-for="n in 8" :key="n" class="card h-14 animate-pulse bg-gray-200/60 dark:bg-gray-700/40"></div>
          </div>

          <div v-else-if="erroCarregamento && !eventos.length" class="card flex flex-col items-center gap-3 p-10 text-center">
            <XCircle class="h-10 w-10 text-red-500" />
            <p class="font-medium text-gray-700 dark:text-gray-200">Não foi possível carregar os eventos.</p>
            <button type="button" class="btn-primary" @click="atualizar">Tentar novamente</button>
          </div>

          <div v-else-if="!eventosFiltrados.length" class="card flex flex-col items-center gap-2 p-10 text-center">
            <Inbox class="h-10 w-10 text-gray-300 dark:text-gray-600" />
            <p class="font-medium text-gray-700 dark:text-gray-200">
              {{ eventos.length ? 'Nenhum evento encontrado com os filtros atuais.' : 'Nenhum evento registrado ainda.' }}
            </p>
            <button v-if="haFiltros" type="button" class="btn-ghost text-blue-600 dark:text-blue-400" @click="limparFiltros">Limpar filtros</button>
            <button v-else-if="podeGerir" type="button" class="btn-primary" @click="abrirCriacao"><Plus class="h-4 w-4" /> Registrar o primeiro evento</button>
          </div>

          <template v-else>
            <!-- Tabela (desktop) -->
            <div class="card hidden overflow-hidden lg:block">
              <div class="table-wrap border-0">
                <table class="w-full text-sm">
                  <thead class="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900/60 dark:text-gray-400">
                    <tr>
                      <th v-for="col in colunas" :key="col.key" scope="col" class="px-4 py-3 font-semibold" :class="col.classe">
                        <button v-if="col.ordenavel" type="button" class="inline-flex items-center gap-1 hover:text-gray-900 dark:hover:text-white" @click="ordenarPor(col.key)">
                          {{ col.label }}
                          <component :is="iconeOrdenacao(col.key)" class="h-3.5 w-3.5" :class="ordem.coluna === col.key ? 'text-blue-600 dark:text-blue-400' : 'opacity-40'" />
                        </button>
                        <span v-else>{{ col.label }}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
                    <tr
                      v-for="evento in eventosPagina"
                      :key="evento.id"
                      class="cursor-pointer transition hover:bg-blue-50/60 dark:hover:bg-gray-700/60"
                      @click="abrirDetalhes(evento.id)"
                    >
                      <td class="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">#{{ evento.id }}</td>
                      <td class="px-4 py-3">
                        <p class="font-medium text-gray-900 dark:text-white">{{ evento.unidade_nome }}</p>
                        <p class="text-xs text-gray-500 dark:text-gray-400">{{ evento.equipamento_nome || 'Sem equipamento' }}</p>
                      </td>
                      <td class="px-4 py-3">
                        <span class="badge" :class="categoriaMeta(evento.categoria_evento).badge">{{ categoriaMeta(evento.categoria_evento).label }}</span>
                      </td>
                      <td class="max-w-[16rem] px-4 py-3">
                        <p class="truncate text-gray-800 dark:text-gray-200" :title="evento.motivo_nome || evento.descricao">{{ evento.motivo_nome || evento.descricao }}</p>
                        <p v-if="evento.motivo_nome" class="truncate text-xs text-gray-500 dark:text-gray-400" :title="evento.descricao">{{ evento.descricao }}</p>
                      </td>
                      <td class="px-4 py-3">
                        <div class="flex flex-wrap items-center gap-1">
                          <span class="badge" :class="statusMeta(evento.status).badge">
                            <span class="h-2 w-2 rounded-full" :class="statusMeta(evento.status).dot"></span>
                            {{ statusMeta(evento.status).label }}
                          </span>
                          <span v-if="evento.atrasado" class="badge bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" title="Previsão de término ultrapassada">Atrasado</span>
                          <span v-if="evento.is_parada && evento.status === 'em_atendimento'" class="badge bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" title="Equipamento parado">Parada</span>
                        </div>
                      </td>
                      <td class="whitespace-nowrap px-4 py-3 text-gray-700 dark:text-gray-300">{{ formatarData(evento.data_inicio) }}</td>
                      <td class="whitespace-nowrap px-4 py-3 text-gray-700 dark:text-gray-300">
                        {{ formatarData(evento.status === 'finalizado' ? evento.data_fechamento || evento.data_final : evento.data_final, 'Sem previsão') }}
                      </td>
                      <td class="whitespace-nowrap px-4 py-3 text-right text-gray-700 dark:text-gray-300">
                        {{ evento.orcamento ? formatarMoeda(evento.orcamento) : '—' }}
                      </td>
                      <td class="px-4 py-3 text-center">
                        <span v-if="evento.total_comentarios" class="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400" :title="`${evento.total_comentarios} comentário(s)`">
                          <MessageSquare class="h-3.5 w-3.5" /> {{ evento.total_comentarios }}
                        </span>
                      </td>
                      <td class="px-4 py-3 text-right">
                        <button type="button" class="btn-ghost px-2 py-1 text-blue-600 dark:text-blue-400" aria-label="Ver detalhes" @click.stop="abrirDetalhes(evento.id)">
                          <Eye class="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Cards (mobile / tablet) -->
            <div class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:hidden">
              <article
                v-for="evento in eventosPagina"
                :key="evento.id"
                class="card relative flex cursor-pointer flex-col gap-2 overflow-hidden p-4 pl-5 transition hover:shadow-md"
                @click="abrirDetalhes(evento.id)"
              >
                <div class="absolute inset-y-0 left-0 w-1.5" :class="statusMeta(evento.status).dot" aria-hidden="true"></div>
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0">
                    <p class="truncate font-semibold text-gray-900 dark:text-white">{{ evento.unidade_nome }}</p>
                    <p class="truncate text-xs text-gray-500 dark:text-gray-400">{{ evento.equipamento_nome || 'Sem equipamento' }} · #{{ evento.id }}</p>
                  </div>
                  <span class="badge shrink-0" :class="statusMeta(evento.status).badge">{{ statusMeta(evento.status).label }}</span>
                </div>
                <p class="line-clamp-2 text-sm text-gray-700 dark:text-gray-300">{{ evento.motivo_nome || evento.descricao }}</p>
                <div class="flex flex-wrap gap-1">
                  <span class="badge" :class="categoriaMeta(evento.categoria_evento).badge">{{ categoriaMeta(evento.categoria_evento).label }}</span>
                  <span v-if="evento.atrasado" class="badge bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Atrasado</span>
                  <span v-if="evento.is_parada && evento.status === 'em_atendimento'" class="badge bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">Parada</span>
                </div>
                <dl class="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <dt class="text-gray-500 dark:text-gray-400">Início</dt>
                    <dd class="font-medium text-gray-800 dark:text-gray-200">{{ formatarData(evento.data_inicio) }}</dd>
                  </div>
                  <div>
                    <dt class="text-gray-500 dark:text-gray-400">{{ evento.status === 'finalizado' ? 'Concluído em' : 'Previsão' }}</dt>
                    <dd class="font-medium text-gray-800 dark:text-gray-200">
                      {{ formatarData(evento.status === 'finalizado' ? evento.data_fechamento || evento.data_final : evento.data_final, 'Sem previsão') }}
                    </dd>
                  </div>
                </dl>
              </article>
            </div>

            <!-- Paginação -->
            <nav v-if="totalPaginas > 1" class="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600 dark:text-gray-300" aria-label="Paginação">
              <p>Página {{ pagina }} de {{ totalPaginas }}</p>
              <div class="flex items-center gap-1">
                <button type="button" class="btn-secondary px-2 py-1" :disabled="pagina === 1" aria-label="Página anterior" @click="pagina--"><ChevronLeft class="h-4 w-4" /></button>
                <button
                  v-for="p in paginasVisiveis"
                  :key="p"
                  type="button"
                  class="btn px-3 py-1"
                  :class="p === pagina ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'"
                  @click="pagina = p"
                >{{ p }}</button>
                <button type="button" class="btn-secondary px-2 py-1" :disabled="pagina === totalPaginas" aria-label="Próxima página" @click="pagina++"><ChevronRight class="h-4 w-4" /></button>
              </div>
            </nav>
          </template>
        </div>
      </div>

      <!-- ====== MODAL: NOVO EVENTO ====== -->
      <Teleport to="body">
        <Transition name="fade">
          <div
            v-if="modalAberto"
            class="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-novo-evento"
            @click.self="fecharCriacao"
            @keydown.esc="fecharCriacao"
          >
            <form
              class="flex max-h-[95vh] w-full flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl animate-fade-in-up dark:bg-gray-800 sm:max-h-[90vh] sm:max-w-3xl sm:rounded-2xl"
              novalidate
              @submit.prevent="salvarEvento"
            >
              <header class="flex items-center justify-between gap-3 bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 text-white sm:px-6">
                <div>
                  <p class="text-xs uppercase tracking-wide text-blue-100">Eventos</p>
                  <h2 id="titulo-novo-evento" class="text-base font-bold sm:text-lg">Novo evento</h2>
                </div>
                <button type="button" class="rounded-full p-1.5 transition hover:bg-white/20" aria-label="Fechar" @click="fecharCriacao">
                  <X class="h-5 w-5" />
                </button>
              </header>

              <div class="flex-1 space-y-6 overflow-y-auto bg-gray-50 p-4 dark:bg-gray-900 sm:p-6">
                <!-- Situação -->
                <fieldset>
                  <legend class="mb-2 text-sm font-semibold text-gray-800 dark:text-gray-100">Situação do evento</legend>
                  <div class="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    <label
                      v-for="op in OPCOES_SITUACAO"
                      :key="op.valor"
                      class="flex cursor-pointer items-start gap-2 rounded-lg border p-3 text-sm transition"
                      :class="form.status === op.valor
                        ? 'border-blue-600 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/30'
                        : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600'"
                    >
                      <input v-model="form.status" type="radio" name="situacao" :value="op.valor" class="mt-0.5" />
                      <span>
                        <span class="block font-medium text-gray-900 dark:text-white">{{ op.label }}</span>
                        <span class="block text-xs text-gray-500 dark:text-gray-400">{{ op.descricao }}</span>
                      </span>
                    </label>
                  </div>
                </fieldset>

                <!-- Local -->
                <fieldset class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <legend class="col-span-full mb-1 text-sm font-semibold text-gray-800 dark:text-gray-100">Onde</legend>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Unidade <span class="text-red-500">*</span></span>
                    <select v-model="form.unidade" class="input" :class="{ 'border-red-500': erros.unidade }" @change="aoMudarUnidade">
                      <option value="" disabled>Selecione a unidade</option>
                      <option v-for="u in unidades" :key="u.id" :value="u.id">{{ u.nome }}</option>
                    </select>
                    <p v-if="erros.unidade" class="mt-1 text-xs text-red-600">{{ erros.unidade }}</p>
                  </label>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Equipamento</span>
                    <select v-model="form.equipamento" class="input" :disabled="!form.unidade || carregandoEquipamentos">
                      <option value="">{{ !form.unidade ? 'Selecione a unidade primeiro' : carregandoEquipamentos ? 'Carregando…' : equipamentos.length ? 'Sem equipamento específico' : 'Unidade sem equipamentos' }}</option>
                      <option v-for="e in equipamentos" :key="e.id" :value="e.id">{{ e.nome }} · {{ equipamentoMeta(e.status).label }}</option>
                    </select>
                  </label>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Entidade <span class="text-red-500">*</span></span>
                    <select v-model="form.entidade" class="input" :class="{ 'border-red-500': erros.entidade }" @change="aoMudarEntidade">
                      <option value="" disabled>Selecione a entidade</option>
                      <option v-for="e in entidades" :key="e.id" :value="e.id">{{ e.nome }}</option>
                    </select>
                    <p v-if="erros.entidade" class="mt-1 text-xs text-red-600">{{ erros.entidade }}</p>
                  </label>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Categoria <span class="text-red-500">*</span></span>
                    <select v-model="form.categoria_evento" class="input" :class="{ 'border-red-500': erros.categoria_evento }">
                      <option v-for="(meta, chave) in CATEGORIA_META" :key="chave" :value="chave">{{ meta.longo }}</option>
                    </select>
                  </label>
                </fieldset>

                <!-- Classificação -->
                <fieldset class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <legend class="col-span-full mb-1 text-sm font-semibold text-gray-800 dark:text-gray-100">Classificação</legend>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Motivo</span>
                    <select v-model="form.motivo_evento" class="input" :disabled="!form.entidade">
                      <option value="">{{ form.entidade ? (motivosDaEntidade.length ? 'Não informado' : 'Nenhum motivo cadastrado para a entidade') : 'Selecione a entidade primeiro' }}</option>
                      <option v-for="m in motivosDaEntidade" :key="m.id" :value="m.id">{{ m.nome }}</option>
                    </select>
                  </label>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Solução</span>
                    <select v-model="form.solucao_evento" class="input" :disabled="!form.entidade">
                      <option value="">{{ form.entidade ? (solucoesDaEntidade.length ? 'Ainda não definida' : 'Nenhuma solução cadastrada para a entidade') : 'Selecione a entidade primeiro' }}</option>
                      <option v-for="s in solucoesDaEntidade" :key="s.id" :value="s.id">{{ s.nome }}</option>
                    </select>
                  </label>
                  <label class="col-span-full block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Descrição <span class="text-red-500">*</span></span>
                    <textarea v-model="form.descricao" rows="3" class="input" :class="{ 'border-red-500': erros.descricao }" placeholder="O que aconteceu / o que será feito"></textarea>
                    <p v-if="erros.descricao" class="mt-1 text-xs text-red-600">{{ erros.descricao }}</p>
                  </label>
                </fieldset>

                <!-- Datas e impacto -->
                <fieldset class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <legend class="col-span-full mb-1 text-sm font-semibold text-gray-800 dark:text-gray-100">Datas e impacto</legend>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                      Data de início <span v-if="form.status !== 'em_atendimento' || form.categoria_evento === 'preventiva'" class="text-red-500">*</span>
                    </span>
                    <input v-model="form.data_inicio" type="date" class="input" :class="{ 'border-red-500': erros.data_inicio }" :min="form.status === 'pendente' ? amanhaISO : undefined" :max="form.status !== 'pendente' ? hojeISO() : undefined" />
                    <p v-if="erros.data_inicio" class="mt-1 text-xs text-red-600">{{ erros.data_inicio }}</p>
                  </label>
                  <div class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                      {{ form.status === 'finalizado' ? 'Data de conclusão' : 'Previsão de término' }}
                      <span v-if="form.categoria_evento === 'preventiva' || form.status === 'finalizado'" class="text-red-500">*</span>
                    </span>
                    <input v-model="form.data_final" type="date" class="input" :class="{ 'border-red-500': erros.data_final }" :disabled="form.sem_previsao" :min="form.data_inicio || undefined" />
                    <label v-if="form.status !== 'finalizado' && form.categoria_evento !== 'preventiva'" class="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <input v-model="form.sem_previsao" type="checkbox" class="h-4 w-4 rounded" /> Sem previsão de término
                    </label>
                    <p v-if="erros.data_final" class="mt-1 text-xs text-red-600">{{ erros.data_final }}</p>
                  </div>
                  <label class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Orçamento</span>
                    <input :value="orcamentoFormatado" type="text" inputmode="numeric" class="input" placeholder="R$ 0,00" @input="aoDigitarOrcamento" />
                  </label>
                  <div v-if="form.status === 'finalizado'" class="block">
                    <span class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Equipamento após o evento <span class="text-red-500">*</span></span>
                    <select v-model="form.status_apos_evento" class="input" :class="{ 'border-red-500': erros.status_apos_evento }">
                      <option value="" disabled>Selecione</option>
                      <option v-for="op in OPCOES_STATUS_APOS" :key="op.valor" :value="op.valor">{{ op.label }} — {{ op.descricao }}</option>
                    </select>
                    <p v-if="erros.status_apos_evento" class="mt-1 text-xs text-red-600">{{ erros.status_apos_evento }}</p>
                  </div>
                  <label
                    v-else
                    class="flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition"
                    :class="form.is_parada ? 'border-red-500 bg-red-50 dark:border-red-500 dark:bg-red-900/20' : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'"
                  >
                    <input v-model="form.is_parada" type="checkbox" class="mt-0.5 h-4 w-4 rounded" :disabled="!form.equipamento" />
                    <span class="text-sm">
                      <span class="block font-medium text-gray-900 dark:text-white">Equipamento parado</span>
                      <span class="block text-xs text-gray-500 dark:text-gray-400">
                        {{ form.equipamento ? 'Marca o tomógrafo como "parado" (sem exames) enquanto o evento estiver em atendimento.' : 'Selecione um equipamento para usar esta opção.' }}
                      </span>
                    </span>
                  </label>
                </fieldset>

                <p v-if="erroEnvio" class="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300">
                  {{ erroEnvio }}
                </p>
              </div>

              <footer class="flex items-center justify-between gap-2 border-t border-gray-200 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:border-gray-700 dark:bg-gray-800 sm:px-6">
                <p class="hidden text-xs text-gray-500 dark:text-gray-400 sm:block">{{ resumoSituacao }}</p>
                <div class="ml-auto flex gap-2">
                  <button type="button" class="btn-secondary" :disabled="isSubmitting" @click="fecharCriacao">Cancelar</button>
                  <button type="submit" class="btn-primary" :disabled="isSubmitting">
                    <Loader2 v-if="isSubmitting" class="h-4 w-4 animate-spin" />
                    {{ isSubmitting ? 'Salvando…' : 'Salvar evento' }}
                  </button>
                </div>
              </footer>
            </form>
          </div>
        </Transition>
      </Teleport>

      <!-- ====== TOAST ====== -->
      <Teleport to="body">
        <Transition name="slide-down">
          <div
            v-if="toast"
            class="fixed left-1/2 top-4 z-[110] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-lg px-4 py-3 text-sm font-medium text-white shadow-lg"
            :class="toast.tipo === 'erro' ? 'bg-red-600' : 'bg-emerald-600'"
            role="status"
          >
            {{ toast.mensagem }}
          </div>
        </Transition>
      </Teleport>
    </template>
  </BaseTemplate>
</template>

<script setup>
import BaseTemplate from '@/views/templates/BaseTemplate.vue';
import { ref, reactive, computed, watch, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import {
  Search, RefreshCw, Plus, Eye, X, Inbox, XCircle, AlertTriangle, OctagonAlert, MessageSquare,
  ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Loader2,
  CalendarClock, Wrench, CheckCircle2,
} from 'lucide-vue-next';
import apiClient from '@/services/authService';
import { useUserStore } from '@/stores/userStore';
import {
  STATUS_META, CATEGORIA_META, OPCOES_STATUS_APOS,
  statusMeta, categoriaMeta, equipamentoMeta, podeGerirEventos,
  formatarData, formatarMoeda, hojeISO, mensagemErro,
  formularioVazio, validarFormulario, montarPayload,
} from './eventos';

const router = useRouter();
const userStore = useUserStore();
const podeGerir = computed(() => podeGerirEventos(userStore.grupos || []));

const OPCOES_SITUACAO = [
  { valor: 'em_atendimento', label: 'Em atendimento', descricao: 'Já começou; o equipamento fica fora de operação.' },
  { valor: 'pendente', label: 'Agendado', descricao: 'Começa em uma data futura; entra em atendimento automaticamente.' },
  { valor: 'finalizado', label: 'Registro retroativo', descricao: 'Evento já concluído, só para histórico.' },
];

// ---------------------------------------------------------------------------
// Estado da listagem
// ---------------------------------------------------------------------------
const eventos = ref([]);
const unidades = ref([]);
const entidades = ref([]);
const motivos = ref([]);
const solucoes = ref([]);
const isLoading = ref(true);
const isRefreshing = ref(false);
const erroCarregamento = ref(false);
const ultimaAtualizacao = ref(null);

const busca = ref('');
const filtros = reactive({ unidade: '', categoria: '', status: '', periodo: '', atrasados: false, paradas: false });
const ordem = reactive({ coluna: 'data_abertura', asc: false });
const pagina = ref(1);
const POR_PAGINA = 25;

const colunas = [
  { key: 'id', label: '#', ordenavel: true },
  { key: 'unidade_nome', label: 'Unidade / Equipamento', ordenavel: true },
  { key: 'categoria_evento', label: 'Categoria', ordenavel: true },
  { key: 'motivo_nome', label: 'Motivo / Descrição', ordenavel: false },
  { key: 'status', label: 'Status', ordenavel: true },
  { key: 'data_inicio', label: 'Início', ordenavel: true },
  { key: 'data_final', label: 'Previsão / Término', ordenavel: true },
  { key: 'orcamento', label: 'Orçamento', ordenavel: true, classe: 'text-right' },
  { key: 'total_comentarios', label: '', ordenavel: false, classe: 'text-center' },
  { key: 'acoes', label: '', ordenavel: false, classe: 'text-right' },
];

// ---------------------------------------------------------------------------
// Carregamento
// ---------------------------------------------------------------------------
async function fetchEventos() {
  try {
    const { data } = await apiClient.get('/api/v1/events/');
    eventos.value = data;
    ultimaAtualizacao.value = new Date();
    erroCarregamento.value = false;
  } catch (error) {
    erroCarregamento.value = true;
    console.error('Erro ao carregar eventos:', error);
  } finally {
    isLoading.value = false;
  }
}

async function fetchAuxiliares() {
  const carregar = async (url, destino) => {
    try {
      const { data } = await apiClient.get(url);
      destino.value = data;
    } catch (error) {
      console.error(`Erro ao carregar ${url}:`, error);
    }
  };
  await Promise.all([
    carregar('/api/v1/enterprises/', unidades),
    carregar('/api/v1/entity/', entidades),
    carregar('/api/v1/events/reasons/', motivos),
    carregar('/api/v1/events/solutions/', solucoes),
  ]);
}

async function atualizar() {
  if (isRefreshing.value) return;
  isRefreshing.value = true;
  try {
    await fetchEventos();
  } finally {
    isRefreshing.value = false;
  }
}

const horaUltimaAtualizacao = computed(() =>
  ultimaAtualizacao.value ? ultimaAtualizacao.value.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''
);

// ---------------------------------------------------------------------------
// Indicadores
// ---------------------------------------------------------------------------
const abertos = computed(() => eventos.value.filter((e) => e.status !== 'finalizado'));
const totalAtrasados = computed(() => eventos.value.filter((e) => e.atrasado).length);
const totalParadas = computed(() => eventos.value.filter((e) => e.status === 'em_atendimento' && e.is_parada).length);
const contar = (status) => eventos.value.filter((e) => e.status === status).length;

const kpis = computed(() => [
  { key: 'em_atendimento', label: 'Em atendimento', valor: contar('em_atendimento'), icon: Wrench, iconBg: 'bg-amber-100 dark:bg-amber-900/40', iconColor: 'text-amber-600 dark:text-amber-300', ring: 'ring-amber-400', filtro: { status: 'em_atendimento' } },
  { key: 'pendente', label: 'Agendados', valor: contar('pendente'), icon: CalendarClock, iconBg: 'bg-gray-200 dark:bg-gray-700', iconColor: 'text-gray-600 dark:text-gray-300', ring: 'ring-gray-400', filtro: { status: 'pendente' } },
  { key: 'paradas', label: 'Equipamentos parados', valor: totalParadas.value, icon: OctagonAlert, iconBg: 'bg-red-100 dark:bg-red-900/40', iconColor: 'text-red-600 dark:text-red-300', ring: 'ring-red-500', filtro: { paradas: true } },
  { key: 'finalizado', label: 'Finalizados', valor: contar('finalizado'), icon: CheckCircle2, iconBg: 'bg-emerald-100 dark:bg-emerald-900/40', iconColor: 'text-emerald-600 dark:text-emerald-300', ring: 'ring-emerald-500', filtro: { status: 'finalizado' } },
]);

const kpiAtivo = (kpi) => (kpi.filtro.paradas ? filtros.paradas : filtros.status === kpi.filtro.status);
function alternarKpi(kpi) {
  if (kpi.filtro.paradas) {
    filtros.paradas = !filtros.paradas;
  } else {
    filtros.status = filtros.status === kpi.filtro.status ? '' : kpi.filtro.status;
  }
}

// ---------------------------------------------------------------------------
// Filtro / ordenação / paginação
// ---------------------------------------------------------------------------
const haFiltros = computed(() =>
  !!(busca.value || filtros.unidade || filtros.categoria || filtros.status || filtros.periodo || filtros.atrasados || filtros.paradas)
);

function limparFiltros() {
  busca.value = '';
  Object.assign(filtros, { unidade: '', categoria: '', status: '', periodo: '', atrasados: false, paradas: false });
}

const normalizar = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const eventosFiltrados = computed(() => {
  const termo = normalizar(busca.value);
  const limite = filtros.periodo ? Date.now() - Number(filtros.periodo) * 86400000 : null;

  const lista = eventos.value.filter((e) => {
    if (filtros.unidade && e.unidade !== Number(filtros.unidade)) return false;
    if (filtros.categoria && e.categoria_evento !== filtros.categoria) return false;
    if (filtros.status && e.status !== filtros.status) return false;
    if (filtros.atrasados && !e.atrasado) return false;
    if (filtros.paradas && !(e.status === 'em_atendimento' && e.is_parada)) return false;
    if (limite && new Date(e.data_abertura).getTime() < limite) return false;
    if (termo) {
      const alvo = normalizar(`${e.id} ${e.unidade_nome} ${e.equipamento_nome} ${e.motivo_nome} ${e.descricao} ${e.criado_por_nome}`);
      if (!alvo.includes(termo)) return false;
    }
    return true;
  });

  const { coluna, asc } = ordem;
  const dir = asc ? 1 : -1;
  return lista.sort((a, b) => {
    let va = a[coluna];
    let vb = b[coluna];
    if (coluna === 'status') {
      const peso = { em_atendimento: 0, pendente: 1, finalizado: 2 };
      va = peso[va] ?? 9; vb = peso[vb] ?? 9;
    }
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb), 'pt-BR', { numeric: true }) * dir;
  });
});

function ordenarPor(coluna) {
  if (ordem.coluna === coluna) {
    ordem.asc = !ordem.asc;
  } else {
    ordem.coluna = coluna;
    ordem.asc = coluna !== 'data_abertura' && coluna !== 'id';
  }
}
const iconeOrdenacao = (coluna) => (ordem.coluna !== coluna ? ArrowUpDown : ordem.asc ? ArrowUp : ArrowDown);

const totalPaginas = computed(() => Math.max(1, Math.ceil(eventosFiltrados.value.length / POR_PAGINA)));
const eventosPagina = computed(() => eventosFiltrados.value.slice((pagina.value - 1) * POR_PAGINA, pagina.value * POR_PAGINA));
const paginasVisiveis = computed(() => {
  const total = totalPaginas.value;
  const inicio = Math.max(1, Math.min(pagina.value - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => inicio + i);
});
watch([eventosFiltrados, totalPaginas], () => {
  if (pagina.value > totalPaginas.value) pagina.value = totalPaginas.value;
});
watch([busca, () => ({ ...filtros })], () => { pagina.value = 1; });

const abrirDetalhes = (id) => router.push({ name: 'DetalhesEvento', params: { id } });

// ---------------------------------------------------------------------------
// Criação
// ---------------------------------------------------------------------------
const modalAberto = ref(false);
const isSubmitting = ref(false);
const form = ref(formularioVazio());
const erros = ref({});
const erroEnvio = ref('');
const equipamentos = ref([]);
const carregandoEquipamentos = ref(false);
const orcamentoFormatado = ref('');

const amanhaISO = computed(() => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
});

const motivosDaEntidade = computed(() => motivos.value.filter((m) => !form.value.entidade || m.entidade_id === Number(form.value.entidade)));
const solucoesDaEntidade = computed(() => solucoes.value.filter((s) => !form.value.entidade || s.entidade_id === Number(form.value.entidade)));

const resumoSituacao = computed(() => {
  const f = form.value;
  if (f.status === 'pendente') return `Entra em atendimento automaticamente em ${formatarData(f.data_inicio, '(data a definir)')}.`;
  if (f.status === 'finalizado') return 'Não altera o status atual do equipamento; fica apenas no histórico.';
  if (!f.equipamento) return 'Sem equipamento vinculado: não altera o status da unidade.';
  return f.is_parada ? 'O equipamento ficará "parado" até a finalização.' : 'O equipamento ficará "em atendimento" até a finalização.';
});

function abrirCriacao() {
  form.value = formularioVazio();
  if (entidades.value.length === 1) form.value.entidade = entidades.value[0].id;
  erros.value = {};
  erroEnvio.value = '';
  orcamentoFormatado.value = '';
  equipamentos.value = [];
  modalAberto.value = true;
}

function fecharCriacao() {
  if (isSubmitting.value) return;
  modalAberto.value = false;
}

async function aoMudarUnidade() {
  form.value.equipamento = '';
  form.value.is_parada = false;
  equipamentos.value = [];
  if (!form.value.unidade) return;
  carregandoEquipamentos.value = true;
  try {
    const { data } = await apiClient.get('/api/v1/equipments/', { params: { unidade_id: form.value.unidade } });
    equipamentos.value = data;
    if (data.length === 1) form.value.equipamento = data[0].id;
  } catch (error) {
    console.error('Erro ao carregar equipamentos:', error);
  } finally {
    carregandoEquipamentos.value = false;
  }
}

function aoMudarEntidade() {
  form.value.motivo_evento = '';
  form.value.solucao_evento = '';
}

function aoDigitarOrcamento(e) {
  const digitos = e.target.value.replace(/\D/g, '');
  if (!digitos) {
    orcamentoFormatado.value = '';
    form.value.orcamento = null;
    return;
  }
  const numero = parseInt(digitos, 10) / 100;
  form.value.orcamento = numero.toFixed(2);
  orcamentoFormatado.value = formatarMoeda(numero);
}

// Regras de dependência entre campos do formulário
watch(() => form.value.status, (status) => {
  if (status === 'pendente') {
    form.value.sem_previsao = false;
    if (!form.value.data_inicio || form.value.data_inicio <= hojeISO()) form.value.data_inicio = amanhaISO.value;
  } else if (status === 'finalizado') {
    form.value.sem_previsao = false;
    form.value.is_parada = false;
    if (!form.value.data_final) form.value.data_final = hojeISO();
    if (!form.value.data_inicio || form.value.data_inicio > hojeISO()) form.value.data_inicio = hojeISO();
  } else if (!form.value.data_inicio || form.value.data_inicio > hojeISO()) {
    form.value.data_inicio = hojeISO();
  }
});
watch(() => form.value.categoria_evento, (categoria) => {
  if (categoria === 'preventiva') form.value.sem_previsao = false;
});
watch(() => form.value.sem_previsao, (sem) => {
  if (sem) form.value.data_final = '';
});
watch(() => form.value.equipamento, (equipamento) => {
  if (!equipamento) form.value.is_parada = false;
});

async function salvarEvento() {
  if (isSubmitting.value) return;
  erros.value = validarFormulario(form.value);
  erroEnvio.value = '';
  if (Object.keys(erros.value).length) {
    erroEnvio.value = 'Revise os campos destacados.';
    return;
  }

  isSubmitting.value = true;
  try {
    const { data } = await apiClient.post('/api/v1/events/', montarPayload(form.value));
    eventos.value.unshift(data);
    modalAberto.value = false;
    mostrarToast(`Evento #${data.id} registrado.`);
    router.push({ name: 'DetalhesEvento', params: { id: data.id } });
  } catch (error) {
    erroEnvio.value = mensagemErro(error, 'Erro ao criar o evento. Verifique os dados enviados.');
    console.error('Erro ao criar evento:', error);
  } finally {
    isSubmitting.value = false;
  }
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------
const toast = ref(null);
let toastTimer = null;
function mostrarToast(mensagem, tipo = 'sucesso') {
  toast.value = { mensagem, tipo };
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => { toast.value = null; }, 3500);
}

onMounted(async () => {
  await Promise.all([fetchEventos(), fetchAuxiliares()]);
});
</script>
